/**
 * The style group being viewed (JF 2026-10-13): its cost sheets, edited together and saved together. Each style stays
 * its own cost sheet on the server — saving the group saves the sheets that changed.
 */
import { create } from "zustand";
import { sheetFp, useStore } from "../../store.ts";
import { api, type GroupInfo, type GroupSheet } from "../../shared/api.ts";
import type { Sheet } from "@ic/engine";

export interface GroupState {
  group: GroupInfo | null;
  /** may this person change the group (owner) */
  mine: boolean;
  sheets: (GroupSheet & { savedFp: string })[];
  missing: string[];
  loading: boolean;
  open(id: string): Promise<void>;
  close(): void;
  /** change one style's sheet (the page and "apply to all" go through here) */
  edit(i: number, fn: (s: Sheet) => void): void;
  dirtyCount(): number;
  saveAll(): Promise<number>;
  setInfo(p: { name?: string; customer?: string }): Promise<void>;
  /** new order / members (ids of the person's own saved sheets) */
  setSheets(ids: string[]): Promise<void>;
  /** a new order of the styles (indexes into sheets): kept on the server, unsaved edits stay (JF 2026-10-16) */
  reorder(order: number[]): Promise<void>;
}

export const useGroup = create<GroupState>((set, get) => ({
  group: null, mine: false, sheets: [], missing: [], loading: false,
  async open(id) {
    set({ loading: true });
    try {
      const r = await api.group(id);
      set({ group: r.group, mine: r.mine, missing: r.missing, sheets: r.sheets.map((x) => ({ ...x, savedFp: sheetFp(x.data) })) });
    } finally { set({ loading: false }); }
  },
  close() { set({ group: null, sheets: [], missing: [], mine: false }); },
  edit(i, fn) {
    set((st) => {
      const sheets = [...st.sheets];
      const x = sheets[i];
      if (!x || !x.mine) return {};
      const data = structuredClone(x.data);
      fn(data);
      sheets[i] = { ...x, data };
      return { sheets };
    });
  },
  dirtyCount() { return get().sheets.filter((x) => x.mine && sheetFp(x.data) !== x.savedFp).length; },
  async saveAll() {
    let n = 0;
    const sheets = [...get().sheets];
    for (const [i, x] of sheets.entries()) {
      if (!x.mine || sheetFp(x.data) === x.savedFp) continue;
      const r = await api.updateSheet(x.id, x.data);
      sheets[i] = { ...x, savedFp: sheetFp(x.data), updatedAt: r.updatedAt };
      n++;
      // the same sheet open in the cost-sheet editor: take the saved version if the editor has no changes of its own
      const st = useStore.getState();
      if (st.sheetId === x.id) {
        if (!st.isDirty()) { st.setSheet(x.data, { id: x.id }); st.markSaved(x.data); }
        else st.toast("这张单在成本表页面也有未保存的修改，请注意不要互相覆盖。/ This sheet also has unsaved changes in the cost-sheet page.");
      }
    }
    set({ sheets });
    return n;
  },
  async setInfo(p) {
    const g = get().group;
    if (!g) return;
    await api.updateGroup(g.id, p);
    set({ group: { ...g, ...p } });
  },
  async reorder(order) {
    const g = get().group;
    if (!g) return;
    const sheets = order.map((i) => get().sheets[i]);
    const sheetIds = [...sheets.map((x) => x.id), ...get().missing];
    set({ sheets, group: { ...g, sheetIds } });
    await api.updateGroup(g.id, { sheetIds });
  },
  async setSheets(ids) {
    const g = get().group;
    if (!g) return;
    await api.updateGroup(g.id, { sheetIds: ids });
    await get().open(g.id);
  },
}));
